import { getGestionCostosSellPrice } from './productSellPrice.js';

export const PRICE_LIST_MODE_PERCENTAGE = 'percentage';
export const PRICE_LIST_MODE_FIXED = 'fixed';
// Modo de UI (no se persiste en pricing_mode): lista 'fixed' con bulk_rule.
// El precio final de cada renglón se materializa en product_prices, así que
// POS / Tienda / Mercado Libre lo leen como cualquier lista de precios fijos.
export const PRICE_LIST_MODE_BULK = 'bulk';

export const PRICE_LIST_BASE_COST = 'cost';
export const PRICE_LIST_BASE_LIST = 'list';

export function isBulkPriceList(priceList) {
  const rule = priceList?.bulk_rule;
  return priceList?.pricing_mode === PRICE_LIST_MODE_FIXED
    && !!rule && typeof rule === 'object' && !Array.isArray(rule);
}

export function getPriceListUiMode(priceList) {
  if (isBulkPriceList(priceList)) return PRICE_LIST_MODE_BULK;
  return getPriceListMode(priceList);
}

export function normalizeBulkRule(rule) {
  const pct = Number(rule?.pct);
  return {
    base: rule?.base === PRICE_LIST_BASE_COST ? PRICE_LIST_BASE_COST : PRICE_LIST_BASE_LIST,
    pct: Number.isFinite(pct) ? pct : 0,
    dynamic: rule?.dynamic !== false,
    apply_to_new: rule?.apply_to_new !== false,
  };
}

export function getPriceListProductRules(priceList) {
  const raw = priceList?.product_rules;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw;
}

export function getProductCostPrice(product) {
  const cost = Number(product?.cost_price);
  return Number.isFinite(cost) ? cost : 0;
}

export function roundMoney(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Precio de una regla de lista masiva. Espejo de public.price_list_rule_price.
 * null = no se puede calcular (renglón manual, o base 0/sin costo).
 */
export function computeRulePrice(product, rule) {
  if (!rule || rule.manual) return null;
  const baseValue = rule.base === PRICE_LIST_BASE_COST
    ? getProductCostPrice(product)
    : getProductBasePrice(product);
  if (!Number.isFinite(baseValue) || baseValue <= 0) return null;
  const pct = Number(rule.pct);
  const price = roundMoney(baseValue * (1 + (Number.isFinite(pct) ? pct : 0) / 100));
  return price > 0 ? price : null;
}

/**
 * Regla efectiva de un producto en una lista masiva: la propia si el renglón
 * fue personalizado (custom/manual), si no la regla masiva de la lista.
 */
export function getEffectiveRule(productId, productRules, bulkRule) {
  const own = productRules?.[String(productId)];
  if (own && (own.custom || own.manual)) return own;
  const bulk = normalizeBulkRule(bulkRule);
  return { base: bulk.base, pct: bulk.pct, dynamic: bulk.dynamic, manual: false, custom: false };
}

/**
 * Producto como lo ve el POS para calcular precios: sell_price en pesos
 * (convertido si está en USD) y sin variantes. Base única para el form de
 * listas y la exportación, así lo que se muestra/manda = lo que cobra la caja.
 */
export function toPosPricingProduct(product, usdExchangeRate) {
  if (!product) return product;
  return { ...product, sell_price: getGestionCostosSellPrice(product, usdExchangeRate), product_variants: [] };
}

/** % implícito de un precio respecto de una base (para renglones manuales). */
export function impliedPercentage(price, baseValue) {
  const p = Number(price);
  const b = Number(baseValue);
  if (!Number.isFinite(p) || !Number.isFinite(b) || b <= 0) return null;
  return roundMoney((p / b - 1) * 100);
}

export function getPriceListMode(priceList) {
  return priceList?.pricing_mode === PRICE_LIST_MODE_FIXED
    ? PRICE_LIST_MODE_FIXED
    : PRICE_LIST_MODE_PERCENTAGE;
}

export function getPriceListProductPrices(priceList) {
  const raw = priceList?.product_prices;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw;
}

export function getProductBasePrice(product) {
  const baseSellPrice = Number(product?.sell_price || 0);
  const variantPrices = Array.isArray(product?.product_variants)
    ? product.product_variants
        .map((variant) => Number(variant?.price))
        // > 0 (no >= 0): las variantes DEFAULT se crean con price:0 como placeholder
        // (ProductoDetalle/GestionCostosImportar) y no se sincronizan con sell_price.
        // Con >= 0, min()=0 hacía que la tienda mostrara/vendiera el producto a $0. Bug P1.
        .filter((price) => Number.isFinite(price) && price > 0)
    : [];

  if (variantPrices.length > 0) {
    return Math.min(...variantPrices);
  }

  return Number.isFinite(baseSellPrice) ? baseSellPrice : 0;
}

export function getVariantAbsolutePrice(product, variant) {
  if (!variant) return getProductBasePrice(product);
  const variantPrice = Number(variant?.price);
  return Number.isFinite(variantPrice) ? variantPrice : getProductBasePrice(product);
}

export function getFixedProductBasePrice(product, priceList) {
  const productId = String(product?.id || '');
  const priceMap = getPriceListProductPrices(priceList);
  const fixedPrice = Number(priceMap?.[productId]);
  return Number.isFinite(fixedPrice) && fixedPrice >= 0 ? fixedPrice : null;
}

/**
 * Precio de un producto en una lista masiva, resuelto al momento de vender.
 * - Regla efectiva = la propia del renglón si se personalizó; si no, el % general
 *   de la lista (también para productos sin regla: nuevos, inactivos, etc.).
 * - "% sobre precio de lista que varía": se calcula con la base que usa quien
 *   consulta (POS: sell_price en pesos, convertido si el producto está en USD),
 *   exactamente como el % global viejo.
 * - Manual / fijo / sobre costo: el $ materializado en product_prices.
 * Devuelve { price, factor, runtime }; runtime=true => el % se aplica también
 * a cada variante.
 */
export function resolveBulkListPrice(product, priceList, basePrice) {
  const key = String(product?.id ?? '');
  const rule = getEffectiveRule(key, getPriceListProductRules(priceList), priceList?.bulk_rule);
  const stored = getFixedProductBasePrice(product, priceList);
  const pct = Number(rule.pct);
  const factor = 1 + (Number.isFinite(pct) ? pct : 0) / 100;

  if (rule.manual) return { price: stored ?? basePrice, factor: 1, runtime: false };

  if (rule.base !== PRICE_LIST_BASE_COST && rule.dynamic !== false && basePrice > 0) {
    return { price: basePrice * factor, factor, runtime: true };
  }

  if (stored != null) return { price: stored, factor, runtime: false };

  // Sin $ guardado (producto sin materializar): se calcula si hay base.
  const cost = Number(product?.cost_price);
  if (rule.base === PRICE_LIST_BASE_COST) {
    return Number.isFinite(cost) && cost > 0
      ? { price: roundMoney(cost * factor), factor, runtime: false }
      : { price: basePrice, factor: 1, runtime: false };
  }
  return { price: basePrice > 0 ? roundMoney(basePrice * factor) : basePrice, factor, runtime: false };
}

export function getPriceListAdjustedBasePrice(product, priceList) {
  const basePrice = getProductBasePrice(product);
  const mode = getPriceListMode(priceList);

  if (!priceList) return basePrice;

  if (mode === PRICE_LIST_MODE_FIXED) {
    if (isBulkPriceList(priceList)) return resolveBulkListPrice(product, priceList, basePrice).price;
    const fixedPrice = getFixedProductBasePrice(product, priceList);
    return fixedPrice ?? basePrice;
  }

  const percentage = Number(priceList?.adjustment_percentage || 0);
  return basePrice * (1 + percentage / 100);
}

export function getPriceListAdjustedVariantPrice(product, variant, priceList) {
  if (!variant) return getPriceListAdjustedBasePrice(product, priceList);

  const mode = getPriceListMode(priceList);
  const basePrice = getProductBasePrice(product);
  const variantPrice = getVariantAbsolutePrice(product, variant);

  if (!priceList) return variantPrice;

  if (mode === PRICE_LIST_MODE_FIXED) {
    if (isBulkPriceList(priceList)) {
      const resolved = resolveBulkListPrice(product, priceList, basePrice);
      // % sobre precio de lista que varía: el % se aplica a cada variante,
      // igual que el % global viejo. Si no, la variante conserva su diferencia.
      if (resolved.runtime) return variantPrice * resolved.factor;
      return resolved.price + (variantPrice - basePrice);
    }
    const fixedBasePrice = getFixedProductBasePrice(product, priceList);
    if (fixedBasePrice == null) return variantPrice;
    return fixedBasePrice + (variantPrice - basePrice);
  }

  const percentage = Number(priceList?.adjustment_percentage || 0);
  return variantPrice * (1 + percentage / 100);
}

/**
 * Devuelve el array de IDs de productos OCULTOS de la lista.
 * - Por defecto, todos los productos son visibles.
 * - Sólo aparecen acá los productos que el admin marcó explícitamente como
 *   ocultos para esta lista (toggle de visibilidad por producto).
 */
export function getPriceListHiddenProductIds(priceList) {
  const raw = priceList?.hidden_product_ids;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => (value == null ? '' : String(value)))
    .filter((value) => value.length > 0);
}

/**
 * ¿El producto se muestra dentro de esta lista de precios?
 * - Sin lista activa → siempre visible (comportamiento histórico).
 * - Con lista activa → visible salvo que esté en hidden_product_ids.
 */
export function isProductVisibleInPriceList(productId, priceList) {
  if (!priceList) return true;
  if (!productId) return true;
  const hidden = getPriceListHiddenProductIds(priceList);
  if (hidden.length === 0) return true;
  return !hidden.includes(String(productId));
}

/**
 * Resuelve la lista de precios efectiva para el POS según el sitio activo
 * y la regla de empresa (use_pos_price_list_per_site).
 *
 * @param {object} params
 * @param {object|null} params.organization - registro organizations
 * @param {object|null} params.site - registro sitios (incluye price_list_id)
 * @param {Array} params.priceLists - listas activas
 * @returns {object|null} la lista a usar o null para precios base
 */
export function resolvePosPriceListForSite({ organization, site, priceLists }) {
  if (!organization?.use_pos_price_list_per_site) return null;
  const targetId = site?.price_list_id;
  if (!targetId) return null;
  const found = (priceLists || []).find((pl) => pl?.id === targetId) || null;
  // Si la lista existe pero está desactivada, no la aplicamos.
  if (!found) return null;
  if (found.is_active === false) return null;
  return found;
}

export function getPriceListSummaryLabel(priceList) {
  if (isBulkPriceList(priceList)) {
    const rule = normalizeBulkRule(priceList.bulk_rule);
    const sign = rule.pct >= 0 ? '+' : '';
    return `${sign}${rule.pct.toFixed(2)}% sobre ${rule.base === PRICE_LIST_BASE_COST ? 'costo' : 'precio de lista'}`;
  }

  const mode = getPriceListMode(priceList);

  if (mode === PRICE_LIST_MODE_FIXED) {
    const priceCount = Object.values(getPriceListProductPrices(priceList)).filter((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && numericValue >= 0;
    }).length;

    return `${priceCount} precio${priceCount === 1 ? '' : 's'} especifico${priceCount === 1 ? '' : 's'}`;
  }

  const percentage = Number(priceList?.adjustment_percentage || 0);
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}% global`;
}
